// What happened around a session's flagged findings (lib/findingStory.js),
// for a reader meeting them for the first time: what the panel says about
// them, the session's card with a pin at each moment, then one compact
// timeline. The person's words are chat bubbles; each flagged call is open at
// its command and what came back, starting at the part a check quoted; the
// agent's other work between two moments folds into one row that opens in
// place. Anything held to a line or two opens whole on "more". Nothing here
// leaves the page, except the control that opens the session.
import { useEffect, useMemo, useRef, useState } from "react";
import { turnMoment } from "../../lib/findingStory.js";
import { ACTION_CHIP } from "./triageParts.jsx";

const DOT = " \u00b7 ";
const TONE = { danger: "var(--danger,#d4503e)", warn: "var(--warn,#c08a2d)" };
// The timeline: its column of times, the icon on its rail.
const RAIL_TIME = 52;
const ICON_BOX = 20;
const GRID = { gridTemplateColumns: `${RAIL_TIME}px ${ICON_BOX}px minmax(0, 1fr)` };
// A quote shorter than this, or most of the text it is in, marks nothing.
const MARK_MIN = 6;
const MARK_MOST = 0.8;
// The panel's quiet link, for "more" under held text and "Show N calls".
const MORE_LINK = "cursor-pointer bg-transparent p-0 text-left text-[11px] leading-[1.4] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";
// The clamp for each number of lines held, written whole so the build keeps them.
const CLAMP = { 1: "line-clamp-1", 2: "line-clamp-2", 3: "line-clamp-3" };
// A held text longer than this many characters a line has more to show.
const LINE_CHARS = 110;
const BUBBLE = "inline-block max-w-full min-w-0 rounded-[10px] rounded-bl-[3px] border border-[color:var(--line-strong)] px-[9px] py-[2px] text-[12px] leading-[1.5] text-[color:var(--ink)]";

const timeOf = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null);
function spanText(ms) {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 6) / 10;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
}

// Who said or did a thing: a person, or the agent.
function WhoIcon({ who, color }) {
  const common = { width: 14, height: 14, viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 1.2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", className: "shrink-0", style: { color } };
  if (who === "person") return <svg {...common} data-story-icon="person"><circle cx="6" cy="4" r="2" /><path d="M2.2 10.5c.6-2 2-3 3.8-3s3.2 1 3.8 3" /></svg>;
  return <svg {...common} data-story-icon="agent"><rect x="2" y="3.4" width="8" height="6.4" rx="1.4" /><path d="M6 1.4v2M4.4 6.4v.4M7.6 6.4v.4" /></svg>;
}

// The quotes worth marking in `text`.
function usefulMarks(text, marks) {
  const len = String(text || "").replace(/\s+/g, " ").trim().length;
  return [...new Set(marks || [])].filter((q) => q.length >= MARK_MIN && text.includes(q) && q.replace(/\s+/g, " ").trim().length < MARK_MOST * len);
}
// `text` with every place a quote stands marked.
function Marked({ text, marks }) {
  const spans = [];
  for (const q of marks) {
    for (let at = text.indexOf(q); at >= 0; at = text.indexOf(q, at + q.length)) spans.push([at, at + q.length]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  const out = [];
  let i = 0;
  for (const [a, b] of spans) {
    if (a < i) continue;
    if (a > i) out.push(text.slice(i, a));
    out.push(<mark key={a} className="rounded-[1px] px-[1px] text-[color:var(--ink)]" style={{ background: "color-mix(in srgb, var(--warn,#c08a2d) 30%, transparent)" }} data-story-mark="">{text.slice(a, b)}</mark>);
    i = b;
  }
  if (i < text.length) out.push(text.slice(i));
  return out;
}

// A text held to `lines` lines until clicked, then whole. Held, it starts at
// the line holding the first marked quote when that sits further down, and
// says how many lines it skipped.
function Held({ text, marks = [], lines = 2, mono = false, className = "" }) {
  const [all, setAll] = useState(false);
  const s = String(text || "");
  const ms = usefulMarks(s, marks);
  let from = 0;
  let lead = null;
  const at = ms.reduce((best, q) => { const i = s.indexOf(q); return i >= 0 && (best < 0 || i < best) ? i : best; }, -1);
  if (at > 0) {
    const above = (s.slice(0, at).match(/\n/g) || []).length;
    if (above >= lines || at > LINE_CHARS * lines) {
      from = s.lastIndexOf("\n", at) + 1;
      if (at - from > LINE_CHARS) from = Math.max(from, s.lastIndexOf(" ", at - 30) + 1);
      const skipped = (s.slice(0, from).match(/\n/g) || []).length;
      lead = skipped ? `\u22ef ${skipped} line${skipped === 1 ? "" : "s"} above${DOT}` : "\u2026 ";
    }
  }
  const shown = s.slice(from);
  const long = from > 0 || shown.split("\n").length > lines || shown.length > LINE_CHARS * lines;
  const type = mono ? "font-mono text-[11px] leading-[1.55]" : "text-[12px] leading-[1.5]";
  const body = `m-0 whitespace-pre-wrap [overflow-wrap:anywhere] text-[color:var(--ink)] ${type} ${className}`;
  if (!long) return <div className={body} data-story-held=""><Marked text={s} marks={ms} /></div>;
  const toggle = (e) => { e.stopPropagation(); setAll((v) => !v); };
  // The text itself opens on a click too; the link is the control a
  // keyboard and a screen reader find.
  return (
    <div className="grid justify-items-start gap-[1px]">
      <div onClick={toggle} title={all ? "Click to show less" : "Click to show all"} className={`${body} cursor-pointer justify-self-stretch ${all ? "" : CLAMP[lines] || CLAMP[2]}`} data-story-held="">
        {all ? <Marked text={s} marks={ms} /> : <>{lead ? <span className="text-[color:var(--muted-soft)]">{lead}</span> : null}<Marked text={shown} marks={ms} /></>}
      </div>
      <button type="button" aria-expanded={all} onClick={toggle} className={MORE_LINK} data-story-more="">{all ? "less" : "more"}</button>
    </div>
  );
}

// One row on the rail: its time, an icon saying person or agent (ringed in
// the finding's tone where flagged), what happened.
function Row({ kind, at, who, tone = null, dim = false, children }) {
  const time = timeOf(at);
  return (
    <div className={`relative grid items-start gap-x-[10px] py-[4px] ${dim ? "opacity-[0.62]" : ""}`} style={GRID} data-story-row={kind}>
      <span className="pt-[3px] text-right text-[10px] leading-[1.4] whitespace-nowrap tabular-nums text-[color:var(--muted)]">{time || ""}</span>
      <span className="relative z-[1] flex items-center justify-center rounded-full bg-[color:var(--panel)]" style={{ width: ICON_BOX, height: ICON_BOX, boxShadow: tone ? `0 0 0 1.5px ${TONE[tone]}` : undefined }}>
        <WhoIcon who={who} color={tone ? TONE[tone] : who === "person" ? "var(--ink)" : "var(--muted)"} />
      </span>
      <div className="grid min-w-0 gap-[3px] [&>*]:min-w-0">{children}</div>
    </div>
  );
}

// What the record says about a call, as it says it: its tool, the exit code
// it printed, whether it came back as an error.
function CallHead({ tool, exitCode, failed }) {
  const chip = "rounded-[2px] border border-[color:var(--line)] px-[4px] font-mono text-[10px] leading-[15px] text-[color:var(--muted)]";
  return (
    <div className="flex flex-wrap items-baseline gap-x-[6px] text-[10.5px] leading-[1.4]" data-story-call-head="">
      <span className="font-mono text-[color:var(--muted)]">{tool}</span>
      {exitCode != null ? <span className={chip}>exit code {exitCode}</span> : null}
      {failed ? <span className={chip}>returned an error</span> : null}
    </div>
  );
}

function Reason({ reason }) {
  if (!reason?.text) return null;
  return (
    <span className="text-[11px] leading-[1.45] text-[color:var(--muted)]" data-story-reason="">
      {reason.escalated ? "Asked to run outside the sandbox: " : "Reason written into the call: "}
      {`\u201c${reason.text}\u201d`}
    </span>
  );
}

function Say({ row }) {
  const tags = row.person ? [] : [row.from];
  return (
    <Row kind="say" at={row.at} who={row.person ? "person" : "agent"} tone={row.cited && !row.person ? "warn" : null}>
      {tags.length ? <span className="text-[11px] leading-[1.4] font-medium text-[color:var(--warn,#c08a2d)]" data-story-tag="">{tags.join(DOT)}</span> : null}
      <div>
        <span className={`${BUBBLE} ${row.person ? "bg-[color:var(--bg)]" : "border-dashed bg-transparent"}`} data-story-bubble={row.person ? "person" : "other"}>
          <Held text={row.text} marks={row.marks} lines={2} />
        </span>
      </div>
    </Row>
  );
}

function Act({ row }) {
  return (
    <Row kind="act" at={row.at} who="agent" tone={row.tone}>
      <CallHead tool={row.tool} exitCode={row.exitCode} failed={row.failed} />
      <div className="grid gap-[3px] pl-[8px]" style={{ borderLeft: `2px solid ${TONE[row.tone]}` }} data-story-act="">
        <Held text={row.command} marks={row.marks} lines={2} mono />
        {row.result != null ? (
          <div className="flex min-w-0 items-baseline gap-x-[6px]">
            <span aria-hidden="true" className="text-[color:var(--muted)]">{"\u21b3"}</span>
            <div className="min-w-0 flex-1"><Held text={row.result || "(no output)"} marks={row.marks} lines={1} mono /></div>
          </div>
        ) : null}
      </div>
      <Reason reason={row.reason} />
    </Row>
  );
}

function Said({ row }) {
  return (
    <Row kind="said" at={row.at} who="agent" tone={row.tone || null}>
      <div className={row.tone ? "pl-[8px]" : ""} style={row.tone ? { borderLeft: `2px solid ${TONE[row.tone]}` } : undefined}>
        <Held text={row.text} marks={row.marks} lines={2} />
      </div>
    </Row>
  );
}

// The agent's other work between two moments, one row until opened.
function Fold({ items }) {
  const [open, setOpen] = useState(false);
  const calls = items.filter((x) => x.kind === "call");
  const notes = items.length - calls.length;
  const counts = new Map();
  for (const c of calls) counts.set(c.tool, (counts.get(c.tool) || 0) + 1);
  const failed = calls.filter((c) => c.failed).length;
  const count = [
    calls.length ? `${calls.length} call${calls.length === 1 ? "" : "s"}` : null,
    notes ? `${notes} message${notes === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(", ");
  const rest = (counts.size ? `${DOT}${[...counts].map(([tool, n]) => (n > 1 ? `${tool} \u00d7${n}` : tool)).join(", ")}` : "")
    + (failed ? `${DOT}${failed} returned an error` : "");
  // Closed, the row reads as the panel's other links: "Show 3 calls".
  return (
    <div className="grid" data-story-fold="">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="group grid cursor-pointer items-center gap-x-[10px] rounded-[2px] bg-transparent py-[3px] text-left hover:bg-[color:var(--control-bg)]" style={GRID} data-story-fold-toggle="">
        <span />
        <span className="flex justify-center text-[10px] text-[color:var(--muted)]" aria-hidden="true" style={{ transform: open ? "rotate(90deg)" : undefined }}>{"\u25b8"}</span>
        <span className="min-w-0 truncate text-[11px] leading-[1.4] text-[color:var(--muted)]" data-story-fold-label="">
          <span className="underline decoration-dotted underline-offset-2 group-hover:text-[color:var(--ink)]">{open ? count : `Show ${count}`}</span>
          {rest}
        </span>
      </button>
      {open ? items.map((x, i) => (
        x.kind === "call" ? (
          <Row key={`c${x.turn}:${x.id || i}`} kind="call" at={x.at} who="agent" dim>
            <CallHead tool={x.tool} exitCode={x.exitCode} failed={x.failed} />
            <Held text={x.command} lines={1} mono />
            {x.result != null ? (
              <div className="flex min-w-0 items-baseline gap-x-[6px]">
                <span aria-hidden="true" className="text-[color:var(--muted)]">{"\u21b3"}</span>
                <div className="min-w-0 flex-1"><Held text={x.result || "(no output)"} lines={1} mono /></div>
              </div>
            ) : null}
          </Row>
        ) : (
          <Row key={`n${x.turn}:${i}`} kind="note" at={x.at} who="agent" dim><Held text={x.text} lines={1} /></Row>
        )
      )) : null}
    </div>
  );
}

// While the story loads: a line with three dots pulsing in turn, for exactly
// as long as the work takes.
export function StoryLoading({ label = "Reading the session" }) {
  return (
    <span aria-busy="true" role="status" data-story-loading="" className="inline-flex items-center gap-x-[3px] py-[2px] text-[11px] leading-[18px] text-[color:var(--muted)]">
      {label}
      {[0, 1, 2].map((i) => (
        <span key={i} aria-hidden="true" className="ml-[1px] h-[4px] w-[4px] rounded-full bg-[color:var(--muted)] animate-pulse motion-reduce:animate-none" style={{ animationDuration: "1s", animationDelay: `${i * 0.18}s` }} data-story-loading-dot="" />
      ))}
    </span>
  );
}

// The timeline's moments the card pins: what was said and each flagged call.
const PINNED = new Set(["say", "act", "said"]);
export const pinTitle = (i) => `m${i}`;
const HIT = 18;
const RING = 20;
const PIN_TIP = 7;
const PREVIEW_W = 300;
const CELL_CONTEXT = "On the card";
const WHO = { person: "The person", notice: "Notice", ran: "Agent ran", result: "Came back", failed: "Failed", said: "Agent said", thought: "Agent thought" };
const PROSE = new Set(["person", "notice", "said", "thought"]);

// What a moment says in a line: its words, or its command.
const rowText = (row) => (row.kind === "act" ? row.command : row.text) || "";
const rowWho = (row) => (row.kind === "say" ? (row.person ? "The person" : row.from || "Not typed by the person") : row.kind === "act" ? row.tool : "Agent said");

// A moment pointed at on the card, under its pin: who and when, what it
// says, and that a click finds it below.
function Preview({ row, left, top }) {
  return (
    <div data-story-preview="" className="pointer-events-none absolute z-10 grid gap-[4px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[9px] py-[7px] shadow-[0_6px_18px_rgba(0,0,0,0.12)]" style={{ left, top, width: PREVIEW_W }}>
      <div className="flex items-center gap-x-[6px] text-[10px] leading-[1.4] text-[color:var(--muted)]">
        <WhoIcon who={row.kind === "say" && row.person ? "person" : "agent"} color={row.tone ? TONE[row.tone] : "var(--muted)"} />
        <span>{[rowWho(row), timeOf(row.at)].filter(Boolean).join(DOT)}</span>
      </div>
      <span className={`line-clamp-4 whitespace-pre-wrap text-[color:var(--ink)] [overflow-wrap:anywhere] ${row.kind === "act" ? "font-mono text-[11px] leading-[1.45]" : "text-[12px] leading-[1.45]"}`}>{rowText(row)}</span>
      <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">Click to find it below</span>
    </div>
  );
}

// A mark on the card that is none of the timeline's moments, read from the
// record: who and when, its words or its call, and a step through the
// messages its mark stands for.
function Cell({ moment, step, onClose }) {
  const link = "cursor-pointer bg-transparent p-0 text-[10px] leading-[1.4] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";
  return (
    <div className="grid gap-[3px] rounded-[2px] border border-[color:var(--line-soft)] px-[7px] py-[5px]" data-story-cell="">
      <div className="flex items-center gap-x-[6px] text-[10px] leading-[1.4] text-[color:var(--muted)]">
        <span>{[WHO[moment.who] || WHO.said, CELL_CONTEXT, timeOf(moment.at)].filter(Boolean).join(DOT)}</span>
        <span className="ml-auto flex items-center gap-x-[8px] tabular-nums">
          {step ? (
            <span className="flex items-center gap-x-[4px]">
              <button type="button" aria-label="Previous message in this mark" onClick={step.prev} className={link}>{"\u2039"}</button>
              {step.label}
              <button type="button" aria-label="Next message in this mark" onClick={step.next} className={link}>{"\u203a"}</button>
            </span>
          ) : null}
          <button type="button" aria-label="Close" onClick={onClose} className={link}>{"\u00d7"}</button>
        </span>
      </div>
      <Held text={moment.quote} marks={moment.highlight ? [moment.highlight] : []} lines={3} mono={!PROSE.has(moment.who)} />
    </div>
  );
}

// `story`: findingStory's; `timeline`: storyTimeline's, over `turns`.
// `findingsFor(indexes, story)`: what the panel says about those findings,
// under each one's title. `summaryFor(story)`: what happened, once for them
// all, above them. `children`: the session's card, between them and the
// timeline, pinning the timeline's moments (titled pinTitle(row)); `anchors`:
// where the card has its pins, turns and cells, once it is laid out.
// `onRing(title | null)`: the pin of the moment picked, for the card to ring.
// `onOpen({ messageId })`: open the session at that message.
//
// On the card, a pin pointed at previews its moment; clicked, it picks the
// moment, which the timeline lights and brings into view. Any other mark
// opens its message over the timeline. A moment pointed at in the timeline
// draws a line up to its pin. The arrow keys step through the moments.
export default function FindingStory({ story, timeline, turns = null, anchors = null, findingsFor = null, summaryFor = null, onOpen = null, onRing = null, children = null }) {
  const flags = story?.flags || [];
  const findings = flags.filter((f) => f.kind === "finding");
  const rows = timeline?.rows || [];
  const moments = useMemo(() => rows.map((r, i) => (PINNED.has(r.kind) ? i : null)).filter((i) => i != null), [rows]);
  // The moment picked from the card; none to begin with.
  const [picked, setPicked] = useState(null);
  const [hover, setHover] = useState(null);
  const [cell, setCell] = useState(null);
  const refs = useRef(new Map());

  // Each moment's pin on the card; a moment whose cell holds another's pin
  // shares it, the card drawing one pin a cell and row.
  const layout = useMemo(() => {
    if (!anchors?.width) return null;
    const byTitle = new Map((anchors.pins || []).map((p) => [p.title, p]));
    const pins = new Map();
    for (const i of moments) {
      const own = byTitle.get(pinTitle(i));
      if (own) { pins.set(i, own); continue; }
      const x = anchors.xOfTurn?.(rows[i].turn) ?? anchors.body?.left ?? 0;
      const near = (anchors.pins || []).reduce((best, p) => (Math.abs(p.x - x) < Math.abs((best?.x ?? Infinity) - x) ? p : best), null);
      pins.set(i, near && Math.abs(near.x - x) <= 8 ? { ...near, shared: true } : { x, y: null, shared: true });
    }
    return { width: anchors.width, pins };
  }, [anchors, moments, rows]);

  const ringed = picked != null && !cell ? pinTitle(picked) : null;
  useEffect(() => { onRing?.(ringed); }, [ringed, onRing]);
  const pick = (i, { scroll = true } = {}) => {
    setCell(null);
    setPicked(i);
    if (scroll && i != null) refs.current.get(i)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  };
  const stepBy = (d) => {
    const at = moments.indexOf(picked);
    const next = at < 0 ? (d > 0 ? 0 : moments.length - 1) : Math.max(0, Math.min(moments.length - 1, at + d));
    pick(moments[next]);
  };
  const onKey = (e) => {
    if (e.target.closest?.("button, [data-story-held]")) return;
    if (e.key === "ArrowRight") { e.preventDefault(); stepBy(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); stepBy(-1); }
    else if (e.key === "Escape" && (picked != null || cell)) { e.preventDefault(); setCell(null); setPicked(null); }
  };
  // A click on the card: a moment's own mark picks it; any other mark opens
  // its message over the timeline.
  const onCard = (e) => {
    if (!anchors?.cellAt || !layout) return;
    const box = e.currentTarget.getBoundingClientRect();
    const hit = anchors.cellAt(e.clientX - box.left, e.clientY - box.top);
    if (!hit) { setCell(null); return; }
    const own = moments.find((i) => hit.turns.includes(rows[i].turn));
    if (own != null) { pick(own); return; }
    setCell({ turns: hit.turns, k: 0, x: hit.x, y: hit.y });
  };
  const stepCell = (d) => setCell((cur) => (cur ? { ...cur, k: Math.max(0, Math.min(cur.turns.length - 1, cur.k + d)) } : cur));
  const cellMoment = cell && turns ? turnMoment(turns, cell.turns[cell.k]) : null;
  // The workspace opens at the message shown: the mark's, the moment picked, else the first flagged one.
  const opensAt = cellMoment?.messageId || rows[picked]?.messageId || timeline?.openAt || null;
  const openChip = onOpen && opensAt ? (
    <button type="button" data-story-open-workspace="" onClick={() => onOpen({ messageId: opensAt })} className={ACTION_CHIP}>
      Open in workspace
    </button>
  ) : null;
  const hoverPin = hover && layout ? layout.pins.get(hover.row) : null;

  const moment = (i, el) => (
    <div
      key={`m${i}`}
      ref={(n) => { if (n) refs.current.set(i, n); else refs.current.delete(i); }}
      onMouseEnter={() => setHover({ row: i, from: "row" })}
      onMouseLeave={() => setHover(null)}
      onClick={() => pick(i, { scroll: false })}
      className={`relative rounded-[2px] ${hover?.from === "card" && hover.row === i ? "bg-[color:var(--control-bg)]" : ""}`}
      data-story-moment={i}
      data-story-lit={hover?.from === "card" && hover.row === i ? "" : undefined}
      data-story-picked={picked === i && !cell ? "" : undefined}
    >
      {el}
    </div>
  );

  return (
    <div data-finding-story="" role="group" aria-label="What happened" tabIndex={0} onKeyDown={onKey} className="grid min-w-0 gap-[6px] outline-none">
      {summaryFor ? <div data-story-summary="">{summaryFor(story)}</div> : null}
      {/* Each finding and what the panel says about it; its title only
          where there are several, the row's head naming a lone one. A
          finding the summary already told is left off. */}
      {findings.map((f) => {
        const said = findingsFor ? findingsFor([f.finding], story) : null;
        if (findingsFor && !said) return null;
        return (
          <div key={f.key} className="grid gap-[3px]" data-story-finding={f.finding}>
            {findings.length > 1 ? <span className="text-[12px] font-medium leading-[1.35] text-[color:var(--ink)]">{f.title || f.label}</span> : null}
            {said ? <div data-story-findings="">{said}</div> : null}
          </div>
        );
      })}
      {/* The session's card, pinning the moments, the control that opens it over its corner. */}
      {children ? (
        <div className="relative min-w-0" data-story-card="">
          {children}
          {layout ? <div aria-hidden="true" onClick={onCard} className="absolute inset-0 cursor-pointer" data-story-card-hit="" /> : null}
          {layout ? (
            <div className="pointer-events-none absolute inset-0" data-story-overlay="">
              {cell ? <span aria-hidden="true" className="absolute rounded-full" style={{ left: cell.x, top: cell.y, width: RING, height: RING, transform: "translate(-50%, -50%)", border: "1.5px solid var(--ink)" }} data-story-ring="" /> : null}
              {/* A moment pointed at in the timeline: a line from under the card up to its pin. */}
              {hover?.from === "row" && hoverPin?.y != null ? (
                <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true" data-story-link="">
                  <line x1={hoverPin.x} x2={hoverPin.x} y1={hoverPin.y + PIN_TIP + 2} y2="100%" stroke={rows[hover.row].tone ? TONE[rows[hover.row].tone] : "var(--muted)"} strokeWidth={1.25} strokeDasharray="3 2" />
                </svg>
              ) : null}
              {moments.map((i) => {
                const p = layout.pins.get(i);
                if (!p || p.shared || p.y == null) return null;
                return (
                  <button
                    key={i}
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={() => pick(i)}
                    onMouseEnter={() => setHover({ row: i, from: "card" })}
                    onMouseLeave={() => setHover(null)}
                    className="pointer-events-auto absolute cursor-pointer rounded-full bg-transparent p-0"
                    style={{ left: p.x - HIT / 2, top: p.y - HIT / 2, width: HIT, height: HIT }}
                    data-story-pin={rows[i].kind}
                  />
                );
              })}
              {hover?.from === "card" && hoverPin?.y != null ? (
                <Preview row={rows[hover.row]} left={Math.max(0, Math.min(layout.width - PREVIEW_W, hoverPin.x - PREVIEW_W / 2))} top={hoverPin.y + HIT / 2 + 4} />
              ) : null}
            </div>
          ) : null}
          {openChip ? <div className="absolute top-[20px] right-[22px]">{openChip}</div> : null}
        </div>
      ) : null}
      <div data-story-panel="" className="grid min-w-0 gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[9px] py-[6px]">
        {!children && openChip ? <div className="flex justify-end">{openChip}</div> : null}
        {cellMoment ? (
          <Cell
            key={`cell:${cell.turns[cell.k]}`}
            moment={cellMoment}
            onClose={() => setCell(null)}
            step={cell.turns.length > 1 ? { label: `${cell.k + 1} / ${cell.turns.length} in this mark`, prev: () => stepCell(-1), next: () => stepCell(1) } : null}
          />
        ) : null}
        <div className="relative grid min-w-0" data-story-timeline="">
          <span aria-hidden="true" className="absolute top-[12px] bottom-[12px] w-px bg-[color:var(--line-soft)]" style={{ left: RAIL_TIME + 10 + ICON_BOX / 2 }} />
          {rows.map((row, i) => {
            if (row.kind === "say") return moment(i, <Say row={row} />);
            if (row.kind === "act") return moment(i, <Act row={row} />);
            if (row.kind === "said") return moment(i, <Said row={row} />);
            if (row.kind === "calls") return <Fold key={`f${row.items[0].turn}`} items={row.items} />;
            return (
              <div key={`g${i}`} className="grid py-[1px]" style={GRID} data-story-gap="">
                <span />
                <span />
                <span className="text-[10.5px] italic leading-[1.4] text-[color:var(--muted-soft)]">{`${spanText(row.ms)} later`}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
